import React, { useCallback, useEffect, useMemo, useState } from 'react'
import MainLayout from '../../../layouts/MainLayout'
import { usePermissions } from '../../../hooks/usePermissions'
import { useNavigate, useLocation } from 'react-router-dom'
import ConfirmationModal from '../../../components/modals/ConfirmationModal'
import useLoader from '../../../hooks/useLoader'
import useNotification from '../../../hooks/useNotification'
import useBaseService from '../../../hooks/services/useBaseService'
import { entities } from '../../../constants/entities'
import PageHeader from '../../../components/PageHeader'
import ListHeader from '../../../components/ListHeader'
import useAction from '../../../hooks/useAction'
import useUrlFilters from '../../../hooks/useUrlFilters'
import useGenericFilter from '../../../hooks/filters/useGenericFilter'
import { transformFiltersForUrl } from '../../../utils/transformFiltersForUrl'
import TableWithInactiveFilter from '../../../components/table/TableWithInactiveFilter'
import FilterCollapse from '../../../components/FilterCollapse'
import useFilterHandlers from '../../../hooks/useFilterHandlers'
import useQueryWithLoader from '../../../hooks/useQueryWithLoader'
import { shipmentStatusColorMap } from '../../../utils/statusColorMap'
import { formatDate, safe } from '../../../utils/objectUtils'
import AutoCompleteFilter from '../../../components/AutoCompleteFilter'
import baseService from '../../../services/baseService'
import {
  ShipmentItemStatus,
  ShipmentItemStatusEnumById,
} from '../../../enums/shipmentItemStatus'
import { Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, CircularProgress, Stack } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleChevronLeft,
  faCircleChevronRight,
  faCircleExclamation,
  faEye,
  faFileInvoice,
  faPrint,
} from '@fortawesome/free-solid-svg-icons'
import { ShipmentStatusEnumById } from '../../../enums/shipment-status-enum'
import { ExportProtocolFinal } from '../../Movements/shipments/components/ExportProtocolFinal'
import { ExportInvoice } from './components/ExportInvoice'
import { UploadInvoiceModal } from './components/UploadInvoiceModal'
import RomaneioDetailsModal from './components/RomaneioDetailsModal'
import { useMutation } from '@tanstack/react-query'
import api from '../../../services/api'
import { downloadBlob } from '../../../utils/downloadBlob'

const PackingList = () => {
  const { canAccess } = usePermissions()
  const canListInvoices = canAccess('Listar notas fiscais de carregamentos')
  const canCreateInvoices = canAccess('Criar notas fiscais de carregamentos')
  const canUpdateInvoices = canAccess('Atualizar notas fiscais de carregamentos')
  const canViewInvoices = canListInvoices || canAccess('Ver carregamentos')
  const canManageInvoices =
    canCreateInvoices || canUpdateInvoices || canAccess('Atualizar carregamentos')
  const navigate = useNavigate()
  const location = useLocation()

  const {
    filters,
    setFilters: applyUrlFilters,
    pagination,
    changePage,
    setPagination,
  } = useUrlFilters(
    {
      deleted_at: false,
    },
    'packing-list'
  )

  const { handleFilterApply, handleFilterClear } =
    useFilterHandlers(applyUrlFilters)

  const { localFilterValues, handleFilterChange, handleSubmit, handleClear } =
    useGenericFilter(
      {
        deleted_at: false,
      },
      handleFilterApply,
      handleFilterClear
    )

  const { get: baseServiceGet } = useBaseService(navigate)
  const { hideLoader, showLoader } = useLoader()
  const { showNotification } = useNotification()

  const {
    data: shipmentsData,
    isLoading,
    isFetching,
    isError,
    error,
  } = useQueryWithLoader({
    queryKey: ['packing-list', filters, pagination],
    queryFn: async () => {
      const filtersTransformed = transformFiltersForUrl(filters)
      const params = {
        filters: filtersTransformed,
        page: pagination.page,
        perPage: pagination.perPage,
      }
      const response = await baseServiceGet(entities.shipments.get, params)
      return response.result
    },
    staleTime: 60 * 1000,
    select: (data) => {
      const shipments =
        data?.data?.map((item) => {
          const base = {
            id: safe(item.id),
            status: safe(item.status?.name),
            status_id: safe(item.status?.id),
            delivery_date: formatDate(item.updated_at),
            created_at: formatDate(item.created_at),
            has_invoice: !!item.has_invoice,
            isDeleted: !!item.deleted_at,
          }

          return {
            ...base,
            status: safe(ShipmentStatusEnumById[item.status?.id]),
            movements:
              item.movements?.map((movement) => ({
                movement_id: safe(movement.id),
                movement_number: safe(movement.number),
                movement_type_id: safe(movement.movement_type?.id),
                movement_type: safe(movement?.movement_type?.name),
                recipient_customer_id: safe(movement?.recipient_customer?.id),
                recipient_customer: safe(movement?.recipient_customer?.alias),
                average_shipment_status:
                  ShipmentItemStatusEnumById[movement?.average_shipment_status],
                average_shipment_status_id: movement?.average_shipment_status,
                destination_order: safe(movement?.destination?.alias),
              })) || [],
          }
        }) || []

      return {
        shipments,
        totalPages: data.last_page,
      }
    },
    placeholderData: (prev) => prev,
  })

  const { shipments = [], totalPages = 1 } = shipmentsData || {}

  const columns = [
    { key: 'id', label: 'Número' },
    { key: 'status', label: 'Status' },
    { key: 'delivery_date', label: 'Confirmado Em' },
    { key: 'created_at', label: 'Criado Em' },
    {
      key: 'has_invoice',
      label: 'NF Anexada',
      render: (value) => {
        return (
          <Chip
            label={value ? 'Sim' : 'Não'}
            size="small"
            color={value ? 'success' : 'warning'}
          />
        )
      },
    },
  ]

  const collapseConfig = {
    key: ['movements'],
    columns: [
      [
        { key: 'movements.movement_number', label: 'Nº Protocolo' },
        { key: 'movements.movement_type', label: 'Tipo de Movimento' },
        { key: 'movements.recipient_customer', label: 'Cliente Destinatário' },
        { key: 'movements.destination_order', label: 'Destino' },
        {
          key: 'movements.average_shipment_status',
          label: 'Status',
          render: (value, item) => {
            const shipment_item_status_id = item?.average_shipment_status_id

            if (
              shipment_item_status_id ===
                ShipmentItemStatus.AGUARDANDO_CONFIRMACAO ||
              !shipment_item_status_id
            ) {
              return (
                <Box sx={{ color: 'warning.main' }}>
                  <FontAwesomeIcon icon={faCircleExclamation} size="xl" />
                </Box>
              )
            }

            if (shipment_item_status_id === ShipmentItemStatus.CANCELADO) {
              return (
                <Box sx={{ color: 'error.main' }}>
                  <FontAwesomeIcon icon={faCircleChevronLeft} size="xl" />
                </Box>
              )
            }

            if (
              shipment_item_status_id !==
              ShipmentItemStatus.AGUARDANDO_CONFIRMACAO
            ) {
              return (
                <Box sx={{ color: 'success.main' }}>
                  <FontAwesomeIcon icon={faCircleChevronRight} size="xl" />
                </Box>
              )
            }

            // Ícone padrão ou nulo se não houver status correspondente
            return null
          },
        },
      ],
    ],
  }

  useEffect(() => {
    isLoading ? showLoader() : hideLoader()
  }, [isLoading, showLoader, hideLoader])

  useEffect(() => {
    if (isError) {
      showNotification(
        'error',
        `Erro ao carregar os romaneios: ${
          error.response?.data?.message || error.message
        }`
      )
    }
  }, [isError, error, showNotification])

  const [openExportProtocol, setOpenExportProtocol] = useState(false)
  const [openExportInvoice, setOpenExportInvoice] = useState(false)
  const [openUploadInvoice, setOpenUploadInvoice] = useState(false)
  const [openRomaneioDetails, setOpenRomaneioDetails] = useState(false)
  const [manageInvoiceOpen, setManageInvoiceOpen] = useState(false)
  const [invoiceGroups, setInvoiceGroups] = useState([])
  const [loadingInvoiceGroups, setLoadingInvoiceGroups] = useState(false)
  const [uploadTarget, setUploadTarget] = useState(null)

  const {
    openModalConfirmation,
    action,
    handleConfirmAction,
    handleCancelConfirmation,
    selectedItem,
    setSelectedItem,
  } = useAction(navigate)

  const handleDownloadRomaneioDocument = useCallback(async (shipment, romaneio, document) => {
    try {
      const response = await api.get(entities.shipments.romaneio.downloadDocument(shipment?.id, romaneio.id, document.id), { responseType: 'blob' })
      const blob = response?.data || response
      const url = URL.createObjectURL(blob)
      const anchor = window.document.createElement('a')
      anchor.href = url
      anchor.download = document.original_file_name || `nota-fiscal-${romaneio.customer_name || romaneio.id}`
      window.document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
      window.setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (error) {
      showNotification('error', error.response?.data?.message || 'Não foi possível baixar esta nota fiscal.')
    }
  }, [showNotification])

  const openInvoiceManager = useCallback(async (shipment) => {
    setSelectedItem(shipment)
    setManageInvoiceOpen(true)
    setLoadingInvoiceGroups(true)
    try {
      const response = await baseServiceGet(entities.shipments.romaneio.get(shipment.id))
      const result = response?.result || response?.romaneio || []
      const groups = Array.isArray(result) ? result : result ? [result] : []
      setInvoiceGroups(groups.map((romaneio) => {
        const activeInvoice = (romaneio.documents || []).find((document) => {
          const type = String(document.document_type || document.type || '').toUpperCase()
          return type === 'NOTA_FISCAL' && !document.deleted_at && document.active !== false
        })
        return { ...romaneio, activeInvoice }
      }))
    } catch (error) {
      setInvoiceGroups([])
      showNotification('error', error?.response?.data?.message || 'Não foi possível carregar os romaneios.')
    } finally {
      setLoadingInvoiceGroups(false)
    }
  }, [baseServiceGet, setSelectedItem, showNotification])

  useEffect(() => {
    if (location.state?.message) {
      showNotification('error', location.state.message)
      navigate(location.pathname, { replace: true, state: {} })
    }
  }, [location.state, showNotification, navigate, location.pathname])

  const inputsfilters = useMemo(
    () => [
      {
        label: 'Cliente Destinatário',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'alias']}
            model="customer"
            value={localFilterValues['movements.recipient_customer.alias']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange(
                'movements.recipient_customer.id',
                val ? { $eq: val } : null
              )
            }}
            placeholder="Filtre pelo nome ou id do cliente"
          />
        ),
      },
      {
        label: 'Destino',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['id', 'name']}
            model="destinationOrder"
            value={localFilterValues['movements.order.destination_order.name']}
            onChange={(selected) => {
              const val = selected?.value
              handleFilterChange(
                'movements.order.destination_order.id',
                val ? { $eq: val } : null
              )
            }}
            placeholder="Filtre pelo nome ou id do destino"
          />
        ),
      },
      {
        label: 'Status',
        component: (
          <AutoCompleteFilter
            service={baseService}
            columnDataBase={['name']}
            model="shipmentStatus"
            extraFilters={{ id: { $notin: [4] } }}
            value={localFilterValues['status.name']}
            onChange={(selected) => handleFilterChange('status.name', selected)}
            placeholder="Filtre pelo status"
            isMulti
            isClearable
          />
        ),
      },
    ],
    [localFilterValues, handleFilterChange]
  )

  const exportInvoice = useMutation({
    mutationFn: async ({ shipmentId }) => {
      showLoader()
      const response = await api.get(
        entities.shipments.documents.getLatestInvoice(shipmentId),
        { responseType: 'blob' }
      )
      return response
    },
    onSuccess: (response) => {
      downloadBlob(response, 'nota-fiscal')
    },
    onError: (error) => {
      console.error('Erro ao buscar nota fiscal:', error)
      showNotification(
        'error',
        error.response?.data?.message ||
          'Não foi possível carregar a nota fiscal.'
      )
    },
    onSettled: () => {
      hideLoader()
    },
  })

  // 2. Handler estabilizado com useCallback para evitar stale closures e re-renders desnecessários
  const handleViewInvoice = useCallback(
    (shipment) => {
      if (!shipment?.id) {
        showNotification('error', 'Carregamento não identificado.')
        return
      }
      // Usamos mutate() em vez de mutateAsync() pois o tratamento de sucesso/erro já está no useMutation
      exportInvoice.mutate({ shipmentId: shipment.id })
    },
    [exportInvoice, showNotification]
  )

  const actions = useMemo(
    () => [
      {
        id: 'details',
        icon: faEye,
        title: 'Ver Detalhes do Romaneio',
        buttonClass: 'btn-info',
        permission: 'Ver carregamentos',
        onClick: (shipment) => {
          setSelectedItem(shipment)
          setOpenRomaneioDetails(true)
        },
        show: (shipment) => !shipment.isDeleted && canAccess('Ver carregamentos'),
      },
      {
        id: 'visualizar_nf',
        icon: faFileInvoice,
        title: 'Baixar Nota Fiscal',
        buttonClass: 'btn-secondary',
        permission: ['Ver carregamentos', 'Listar notas fiscais de carregamentos'],
        onClick: handleViewInvoice,
        show: (shipment) =>
          !shipment.isDeleted &&
          shipment.has_invoice &&
          canViewInvoices,
      },
      {
        id: 'exportar_romaneio',
        icon: faPrint,
        title: 'Imprimir Romaneio',
        buttonClass: 'btn-warning',
        permission: 'Ver carregamentos',
        onClick: (shipment) => {
          setOpenExportInvoice(true)
          setSelectedItem(shipment)
        },
        show: (shipment) =>
          !shipment.isDeleted &&
          shipment.uploaded_invoice &&
          canAccess('Ver carregamentos'),
      },
      {
        id: 'exportar_protocolo',
        icon: faPrint,
        title: 'Imprimir protocolo de entrega',
        buttonClass: 'btn-success',
        permission: 'Ver carregamentos',
        onClick: (shipment) => {
          setOpenExportProtocol(true)
          setSelectedItem(shipment)
        },
        show: (shipment) =>
          !shipment.isDeleted && canAccess('Ver carregamentos'),
      },
      {
        id: 'gerenciar_nfs',
        icon: faFileInvoice,
        title: 'Gerenciar NFs',
        buttonClass: 'btn-primary',
        permission: [
          'Atualizar carregamentos',
          'Criar notas fiscais de carregamentos',
          'Atualizar notas fiscais de carregamentos',
        ],
        onClick: openInvoiceManager,
        show: (shipment) => !shipment.isDeleted && canManageInvoices,
      },
    ],
    [setSelectedItem, setOpenRomaneioDetails, handleViewInvoice, canAccess, canViewInvoices, canManageInvoices, openInvoiceManager]
  )

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader title="Romaneios" showBackButton backUrl="/dashboard" />
      <div className="container-fluid p-1">
        <FilterCollapse
          autoCompleteFields={inputsfilters}
          onSubmit={handleSubmit}
          onClear={handleClear}
        />

        <ListHeader
          title="Anexar NF dos Romaneios"
          buttonIsNotVisible={true}
          canAccess={canAccess}
        />

        <TableWithInactiveFilter
          data={shipments}
          columns={columns}
          collapseConfig={collapseConfig}
          actions={actions}
          totalPages={totalPages}
          currentPage={pagination.page - 1}
          onPageChange={changePage}
          rowsPerPage={pagination.perPage}
          onRowsPerPageChange={(event) => {
            const newPerPage = +event.target.value
            setPagination({ page: 1, perPage: newPerPage })
          }}
          filterValues={localFilterValues}
          localFilterValues={localFilterValues}
          applyUrlFilters={applyUrlFilters}
          type="collapse"
          quantitySpaces={-1}
          rowColorConfig={{
            colorMap: shipmentStatusColorMap,
            propertyKey: 'status',
          }}
          isLoading={isLoading}
          isFetching={isFetching}
        />

        <ExportProtocolFinal
          data={selectedItem}
          open={openExportProtocol}
          onClose={() => setOpenExportProtocol(false)}
        />

        <ExportInvoice
          data={selectedItem}
          open={openExportInvoice}
          onClose={() => setOpenExportInvoice(false)}
        />

        <RomaneioDetailsModal
          open={openRomaneioDetails}
          data={selectedItem}
          onClose={() => setOpenRomaneioDetails(false)}
          canDownloadInvoices={canViewInvoices}
          canManageInvoices={canManageInvoices}
          onManageInvoices={(romaneio) => {
            setOpenRomaneioDetails(false)
            const activeInvoice = (romaneio.documents || []).find((document) => {
              const type = String(document.document_type || document.type || '').toUpperCase()
              return type === 'NOTA_FISCAL' && !document.deleted_at && document.active !== false
            })
            setUploadTarget({ ...romaneio, activeInvoice })
          }}
          onDownloadDocument={(romaneio, document) => handleDownloadRomaneioDocument(selectedItem, romaneio, document)}
        />

        <UploadInvoiceModal
          data={selectedItem}
          open={openUploadInvoice || Boolean(uploadTarget)}
          onClose={() => { setOpenUploadInvoice(false); setUploadTarget(null) }}
          romaneioId={uploadTarget?.id}
          romaneioLabel={uploadTarget?.customer_name}
          isReplacement={Boolean(uploadTarget?.activeInvoice)}
        />

        <Dialog open={manageInvoiceOpen} onClose={() => setManageInvoiceOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Notas Fiscais por romaneio</DialogTitle>
          <DialogContent dividers>
            {loadingInvoiceGroups ? <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}><CircularProgress size={28} /></Box> : invoiceGroups.length ? <Stack spacing={1.5}>
              {invoiceGroups.map((group) => <Box key={group.id} sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}>
                <Typography fontWeight={600}>{group.customer_name || `Romaneio ${group.id}`}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>{group.customer_address || group.type || ''}</Typography>
                <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                  <Chip size="small" color={group.activeInvoice ? 'success' : 'warning'} label={group.activeInvoice ? 'NF anexada' : 'NF pendente'} />
                   {(group.activeInvoice ? (canUpdateInvoices || canAccess('Atualizar carregamentos')) : (canCreateInvoices || canAccess('Atualizar carregamentos'))) ? <Button size="small" variant={group.activeInvoice ? 'outlined' : 'contained'} color={group.activeInvoice ? 'warning' : 'primary'} onClick={() => { setUploadTarget(group); setManageInvoiceOpen(false) }}>
                     {group.activeInvoice ? 'Substituir NF' : 'Anexar NF'}
                   </Button> : null}
                </Stack>
              </Box>)}
            </Stack> : <Typography>Nenhum romaneio encontrado para este carregamento.</Typography>}
          </DialogContent>
          <DialogActions><Button onClick={() => setManageInvoiceOpen(false)}>Fechar</Button></DialogActions>
        </Dialog>

        <ConfirmationModal
          open={openModalConfirmation}
          onClose={handleCancelConfirmation}
          onConfirm={handleConfirmAction}
          text={action?.text}
        />
      </div>
    </MainLayout>
  )
}

export default PackingList
